// components/tablon/TablonAnunciosPanel.jsx
import React, { useState } from "react";
import {
    Drawer, Box, Typography, IconButton, Divider,
    List, ListItem, Chip, Tooltip, CircularProgress,
    ToggleButton, ToggleButtonGroup, Button, Dialog,
    DialogTitle, DialogContent, TextField, Collapse
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ReplyIcon from "@mui/icons-material/Reply";
import SendIcon from "@mui/icons-material/Send";
import TablonAnunciosForm from "./TablonAnunciosForm";
import { UrlApi } from "../../services/apirest";

const COLORES_PRIORIDAD = {
    normal: { color: "default", label: "Normal" },
    importante: { color: "warning", label: "Importante" },
    urgente: { color: "error", label: "Urgente" },
}

function AnuncioItem({ anuncio, onMarcarVisto, esEnviado }) {
    const [respondiendo, setRespondiendo] = useState(false)
    const [textoResp, setTextoResp] = useState("")
    const [enviando, setEnviando] = useState(false)

    const prioridad = COLORES_PRIORIDAD[anuncio.prioridad] || COLORES_PRIORIDAD.normal
    const fecha = anuncio.created_at
        ? new Date(anuncio.created_at).toLocaleDateString("es-AR", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit"
        })
        : ""

    const handleResponder = async () => {
        if (!textoResp.trim()) return
        setEnviando(true)
        try {
            await fetch(`${UrlApi}/tablon-anuncios`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    titulo: `Re: ${anuncio.titulo}`,
                    mensaje: textoResp,
                    tipo: "individual",
                    username_dest: anuncio.creado_por,
                    prioridad: "normal",
                    fecha_exp: null,
                    creado_por: localStorage.getItem("user")
                })
            })
            setTextoResp("")
            setRespondiendo(false)
        } catch (err) {
            console.error("Error al responder:", err)
        } finally {
            setEnviando(false)
        }
    }

    return (
        <ListItem
            alignItems="flex-start"
            sx={{
                bgcolor: anuncio.visto ? "transparent" : "action.hover",
                borderLeft: anuncio.visto ? "3px solid transparent" : "3px solid #1976d2",
                mb: 1,
                borderRadius: 1,
                flexDirection: "column",
                gap: 0.5
            }}
        >
            {/* Título + checkbox */}
            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                <Typography variant="subtitle2" fontWeight={anuncio.visto ? "normal" : "bold"}>
                    {anuncio.titulo}
                </Typography>
                {!esEnviado && (
                    !anuncio.visto ? (
                        <Tooltip title="Marcar como leído">
                            <IconButton size="small" onClick={() => onMarcarVisto(anuncio.id)}>
                                <RadioButtonUncheckedIcon fontSize="small" color="primary" />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <DoneAllIcon fontSize="small" sx={{ color: "text.disabled" }} />
                    )
                )}
            </Box>

            {/* Mensaje */}
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                {anuncio.mensaje}
            </Typography>

            {/* Footer: chips + fecha + vistos */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.5, flexWrap: "wrap" }}>
                <Chip label={prioridad.label} color={prioridad.color} size="small" variant="outlined" />
                {esEnviado && (
                    <Chip
                        label={anuncio.tipo === "general" ? "General" : `Para: ${anuncio.username_dest}`}
                        size="small"
                        color={anuncio.tipo === "general" ? "success" : "info"}
                        variant="outlined"
                    />
                )}
                {anuncio.tipo === "individual" && !esEnviado && (
                    <Chip label="Personal" size="small" color="info" variant="outlined" />
                )}
                {anuncio.expirado === 1 && (
                    <Chip
                        label="Expirado"
                        size="small"
                        sx={{
                            bgcolor: anuncio.visto === 0 ? "#d32f2f" : "#f5f5f5",
                            color: anuncio.visto === 0 ? "#ffffff" : "#9e9e9e",
                            borderColor: anuncio.visto === 0 ? "#d32f2f" : "#e0e0e0"
                        }}
                        variant="outlined"
                    />
                )}
                <Typography variant="caption" color="text.disabled">
                    {fecha} — por {anuncio.creado_por}
                </Typography>
                {anuncio.total_vistos !== undefined && (
                    <Typography variant="caption" color="text.secondary">
                        Visto por {anuncio.total_vistos} usuario{anuncio.total_vistos !== 1 ? "s" : ""}
                    </Typography>
                )}
            </Box>

            {/* Botón responder — no en enviados, no si soy el autor */}
            {!esEnviado && anuncio.creado_por !== localStorage.getItem("user") && (
                <Box sx={{ width: "100%", mt: 0.5 }}>
                    <Button
                        size="small"
                        startIcon={<ReplyIcon />}
                        onClick={() => setRespondiendo(!respondiendo)}
                        sx={{ textTransform: "none", p: 0 }}
                    >
                        {respondiendo ? "Cancelar" : "Responder"}
                    </Button>

                    <Collapse in={respondiendo}>
                        <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "flex-start" }}>
                            <TextField
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                placeholder={`Responder a ${anuncio.creado_por}...`}
                                value={textoResp}
                                onChange={e => setTextoResp(e.target.value)}
                            />
                            <IconButton
                                color="primary"
                                onClick={handleResponder}
                                disabled={enviando || !textoResp.trim()}
                            >
                                <SendIcon />
                            </IconButton>
                        </Box>
                    </Collapse>
                </Box>
            )}
        </ListItem>
    )
}

export default function TablonAnunciosPanel({ open, onClose, anuncios, cargando, marcarVisto, refetch, enviados, refetchEnviados }) {
    const [filtro, setFiltro] = useState("noleidos")
    const [openForm, setOpenForm] = useState(false)

    const esAdmin = ["admin", "superusuario"].includes(localStorage.getItem("role"))

    const handleFiltro = (_, val) => {
        if (!val) return
        setFiltro(val)
        if (val === "enviados") refetchEnviados()
    }

    const anunciosFiltrados = filtro === "enviados"
        ? enviados
        : filtro === "todos"
            ? anuncios
            : anuncios.filter(a => a.visto === 0)  // ya trae expirados porque quitamos el filtro en backend

    const mensajeVacio = filtro === "todos"
        ? "No hay anuncios activos"
        : filtro === "noleidos"
            ? "No tenés anuncios sin leer"
            : "No enviaste ningún anuncio"

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: { width: { xs: "100%", sm: 420 }, display: "flex", flexDirection: "column" }
                }}
            >
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5 }}>
                    <Typography variant="h6">Tablón de anuncios</Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Divider />

                {/* Botón publicar — solo admin */}
                {esAdmin && (
                    <Box sx={{ px: 2, py: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() => setOpenForm(true)}
                            fullWidth
                        >
                            Publicar anuncio
                        </Button>
                    </Box>
                )}

                {esAdmin && <Divider />}

                {/* Filtros */}
                <Box sx={{ px: 2, py: 1 }}>
                    <ToggleButtonGroup
                        value={filtro}
                        exclusive
                        onChange={handleFiltro}
                        size="small"
                    >
                        <ToggleButton value="todos">Todos</ToggleButton>
                        <ToggleButton value="noleidos">No leídos</ToggleButton>
                        {esAdmin && <ToggleButton value="enviados">Enviados</ToggleButton>}
                    </ToggleButtonGroup>
                </Box>

                <Divider />

                {/* Lista */}
                <Box sx={{ flex: 1, overflowY: "auto", px: 1, py: 1 }}>
                    {cargando ? (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : anunciosFiltrados.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
                            {mensajeVacio}
                        </Typography>
                    ) : (
                        <List disablePadding>
                            {anunciosFiltrados.map(anuncio => (
                                <AnuncioItem
                                    key={anuncio.id}
                                    anuncio={anuncio}
                                    onMarcarVisto={marcarVisto}
                                    esEnviado={filtro === "enviados"}
                                />
                            ))}
                        </List>
                    )}
                </Box>
            </Drawer>

            {/* Dialog formulario — solo admin */}
            {esAdmin && (
                <Dialog
                    open={openForm}
                    onClose={() => setOpenForm(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        Publicar anuncio
                        <IconButton
                            onClick={() => setOpenForm(false)}
                            sx={{ position: "absolute", right: 8, top: 8 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <TablonAnunciosForm onPublicado={() => setOpenForm(false)} />
                    </DialogContent>
                </Dialog>
            )}
        </>
    )
}